import React from 'react';
import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { loadGoogleMapsApi } from '../../utils/googleMaps';
import { MapPin, Clock, Truck, Star, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

export default function FloristsSydney() {
  const mapRef = useRef<HTMLDivElement>(null);
  const [mapError, setMapError] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    const initMap = async () => {
      try {
        await loadGoogleMapsApi();
        
        if (mapRef.current && window.google) {
          const map = new google.maps.Map(mapRef.current, {
            center: { lat: -33.8688, lng: 151.2093 }, // Sydney CBD coordinates
            zoom: 11,
            styles: [
              {
                featureType: "poi",
                elementType: "labels",
                stylers: [{ visibility: "off" }]
              }
            ]
          });

          // Add marker for Sydney CBD
          new google.maps.Marker({
            position: { lat: -33.8688, lng: 151.2093 },
            map: map,
            title: "Sydney CBD",
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 10,
              fillColor: "brand-primary",
              fillOpacity: 0.8,
              strokeColor: "#fff",
              strokeWeight: 2
            }
          });
        }
      } catch (error) {
        console.error('Error loading map:', error);
        setMapError(true);
      }
    };

    initMap();
  }, []);

  const sydneyFloristsSearchUrl = "/search?location=Sydney+NSW%2C+Australia&minPrice=0&maxPrice=500&deliveryType=delivery&lat=-33.8688&lng=151.2093";

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How do I find the best florists in Sydney?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "MarketBloom connects you with Sydney's top-rated independent florists. Look for florists with excellent reviews, fresh portfolio work, and those who source directly from Sydney Flower Market."
        }
      },
      {
        "@type": "Question",
        "name": "Do Sydney florists offer same-day delivery?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, most Sydney florists on MarketBloom offer same-day delivery when orders are placed before 2pm on weekdays across metropolitan Sydney."
        }
      },
      {
        "@type": "Question",
        "name": "What areas do Sydney florists deliver to?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Sydney florists deliver across all metropolitan areas including CBD, Eastern Suburbs, Inner West, North Shore, Northern Beaches, and Western Sydney."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Florists Sydney | Same-Day Flower Delivery | MarketBloom</title>
        <meta name="description" content="Discover Sydney's best independent florists offering fresh flowers, custom arrangements, and same-day delivery across all Sydney suburbs. Support local flower shops." />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>
      
      <div className="pt-20 bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="bg-white border-b">
          <div className="max-w-4xl mx-auto px-6 py-8">
            <div className="flex items-center gap-3 text-sm text-black mb-4">
              <span className="bg-[#967F81] text-white px-3 py-1 rounded-full font-medium">Local Guides</span>
              <span>•</span>
              <span>15 January 2025</span>
              <span>•</span>
              <span>5 min read</span>
            </div>
            <h1 className="scroll-m-20 text-4xl font-extrabold tracking-tight text-black lg:text-5xl">
              Best Florists in Sydney for Same-Day Delivery 2025
            </h1>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-6 py-12">
          
          {/* Hero Image */}
          <div className="mb-12">
            <img 
              src="/images/blog/best-florists-newtown-same-day-hero.jpg" 
              alt="Professional florist creating beautiful arrangement in Sydney flower shop"
              className="w-full h-80 object-cover rounded-lg shadow-lg"
            />
          </div>

          {/* Introduction */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <p className="text-xl leading-7 text-black mb-6">
              I think you'll agree with me when I say:
            </p>
            <p className="leading-7 text-black mb-6">
              Finding exceptional florists in Sydney who deliver same-day shouldn't mean settling for overpriced corporate chains or generic supermarket bouquets.
            </p>
            <p className="leading-7 text-black mb-6">
              That's why MarketBloom exclusively partners with Sydney's most talented independent florists - the passionate artisans from Circular Quay's premium studios to Parramatta's family-run shops who understand that every arrangement tells a story and every delivery matters.
            </p>
            <p className="leading-7 text-black">
              Whether you need urgent flower delivery to a CBD office, a romantic surprise in the Eastern Suburbs, or celebration flowers for someone in the Inner West, our curated network of Sydney's finest florists ensures exceptional quality, artistic creativity, and reliable service across all metropolitan areas.
            </p>
          </div>

          {/* Quick Action CTA */}
          <div className="bg-[#967F81] text-white rounded-lg p-6 mb-12 text-center">
            <h2 className="text-2xl font-bold mb-3">Need Flowers Today?</h2>
            <p className="text-lg mb-4 opacity-90">Browse Sydney's finest florists offering same-day delivery options</p>
            <Link 
              to={`${sydneyFloristsSearchUrl}&utm_source=seo&utm_medium=landing&utm_campaign=sydney-florists`}
              className="bg-[#843329] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#6b2820] transition-colors inline-flex items-center gap-2 min-h-[48px]"
            >
              Shop Sydney Florists
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          {/* What Makes Sydney Florists Special */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">What Makes Sydney Florists Special</h2>
            
            <p className="leading-7 text-black mb-6">
              Sydney's independent florists represent the pinnacle of Australian floral artistry. From the sophisticated studios in Double Bay to the creative workshops in Surry Hills, these talented professionals combine international techniques with uniquely Australian flair, creating arrangements that capture both global trends and local character.
            </p>
            <p className="leading-7 text-black mb-6">
              Unlike generic flower delivery services, Sydney's independent florists source directly from the Sydney Flower Market, often visiting multiple times per week to hand-select the freshest blooms. They understand Sydney's diverse architecture, from harbourside penthouses to heritage terraces, and create arrangements that complement each unique setting perfectly.
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center p-4">
                <div className="bg-[#967F81]/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Star className="w-8 h-8 text-brand-primary" aria-label="Icon for artistic excellence" />
                </div>
                <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Artistic Excellence</h3>
                <p className="text-black">Award-winning designers with international training</p>
              </div>
              <div className="text-center p-4">
                <div className="bg-[#967F81]/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-8 h-8 text-brand-primary" aria-label="Icon for city-wide expertise" />
                </div>
                <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">City-Wide Expertise</h3>
                <p className="text-black">Deep knowledge of all Sydney neighborhoods</p>
              </div>
              <div className="text-center p-4">
                <div className="bg-[#967F81]/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-brand-primary" aria-label="Icon for premium quality" />
                </div>
                <h3 className="scroll-m-20 text-xl font-semibold tracking-tight text-black mb-2">Premium Quality</h3>
                <p className="text-black">Hand-selected blooms from Sydney's best markets</p>
              </div>
            </div>
          </div>

          {/* Same-Day Delivery Coverage */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">Same-Day Delivery Coverage</h2>
            
            <p className="leading-7 text-black mb-6">
              Sydney's best florists offer comprehensive same-day delivery across the entire metropolitan area. With strategic locations from the CBD to outer suburbs, our network ensures reliable, professional delivery service no matter where your flowers need to go.
            </p>

            <div className="mb-6">
              {mapError ? (
                <div className="bg-gray-100 h-64 rounded-lg flex items-center justify-center">
                  <p className="text-gray-500">Map temporarily unavailable</p>
                </div>
              ) : (
                <div ref={mapRef} className="h-64 rounded-lg shadow-sm"></div>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <Link to="/blog/best-florists-bondi-same-day" className="bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-lg text-center transition-colors">
                Bondi
              </Link>
              <Link to="/blog/best-florists-newtown-same-day" className="bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-lg text-center transition-colors">
                Newtown
              </Link>
              <Link to="/blog/best-florists-surry-hills-same-day" className="bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-lg text-center transition-colors">
                Surry Hills
              </Link>
              <Link to="/blog/best-florists-parramatta-same-day" className="bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-lg text-center transition-colors">
                Parramatta
              </Link>
              <span className="bg-gray-100 px-3 py-2 rounded-lg text-center">CBD</span>
              <span className="bg-gray-100 px-3 py-2 rounded-lg text-center">Eastern Suburbs</span>
              <span className="bg-gray-100 px-3 py-2 rounded-lg text-center">Inner West</span>
              <span className="bg-gray-100 px-3 py-2 rounded-lg text-center">North Shore</span>
            </div>
          </div>

          {/* Popular Flower Styles */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">Popular Flower Styles in Sydney</h2>
            
            <p className="leading-7 text-black mb-6">
              Sydney florists are renowned for their versatility and innovation, creating everything from classic European-style arrangements to contemporary Australian native designs. Our florists stay ahead of global trends while celebrating local botanical heritage.
            </p>

            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="font-semibold text-black mb-4">Most Requested Styles:</h3>
              <ul className="space-y-2 text-black">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#967F81] rounded-full"></span>
                  Luxury European-style arrangements with premium imported flowers
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#967F81] rounded-full"></span>
                  Native Australian bouquets featuring waratahs, banksias, and eucalyptus
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#967F81] rounded-full"></span>
                  Modern minimalist designs for contemporary Sydney homes
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#967F81] rounded-full"></span>
                  Corporate arrangements for CBD offices and business events
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-[#967F81] rounded-full"></span>
                  Seasonal mixed bouquets celebrating Sydney's year-round blooming season
                </li>
              </ul>
            </div>
          </div>

          {/* Why MarketBloom Champions Sydney Florists */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">Why MarketBloom Champions Sydney Florists</h2>
            
            <p className="leading-7 text-black mb-6">
              We believe Sydney deserves access to the world's finest floral artistry. That's why we exclusively partner with independent florists who demonstrate exceptional skill, creativity, and commitment to their craft.
            </p>

            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="bg-[#967F81]/10 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-brand-primary font-bold">1</span>
                </div>
                <div>
                  <h3 className="font-semibold text-black mb-2">Rigorous Quality Standards</h3>
                  <p className="text-black">Every florist undergoes thorough vetting for artistic skill, reliability, and customer service excellence.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="bg-[#967F81]/10 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-brand-primary font-bold">2</span>
                </div>
                <div>
                  <h3 className="font-semibold text-black mb-2">Continuous Innovation</h3>
                  <p className="text-black">Our florists stay current with international trends while pioneering uniquely Australian floral styles.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="bg-[#967F81]/10 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-brand-primary font-bold">3</span>
                </div>
                <div>
                  <h3 className="font-semibold text-black mb-2">Sustainable Practices</h3>
                  <p className="text-black">Commitment to environmentally responsible sourcing and packaging across our entire network.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="bg-[#967F81]/10 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-brand-primary font-bold">4</span>
                </div>
                <div>
                  <h3 className="font-semibold text-black mb-2">Professional Excellence</h3>
                  <p className="text-black">Many of our florists hold international certifications and have trained with master florists worldwide.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="bg-[#967F81]/10 w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-brand-primary font-bold">5</span>
                </div>
                <div>
                  <h3 className="font-semibold text-black mb-2">Customer-Centric Service</h3>
                  <p className="text-black">Personalized attention, custom consultations, and ongoing relationships that go beyond single transactions.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Explore Other Areas */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">Explore Florists in Other Sydney Suburbs</h2>
            
            <p className="leading-7 text-black mb-6">
              Each Sydney neighborhood has its own character, and our florists reflect that diversity. Discover what makes each area's floral scene unique:
            </p>

            <div className="grid md:grid-cols-2 gap-4">
              <Link 
                to="/blog/best-florists-bondi-same-day" 
                className="block p-4 border rounded-lg hover:bg-gray-50 transition-colors"
              >
                <h3 className="font-semibold text-black mb-2">Bondi Florists</h3>
                <p className="text-sm text-gray-600">Coastal-inspired arrangements near Australia's most famous beach</p>
              </Link>
              <Link 
                to="/blog/best-florists-newtown-same-day" 
                className="block p-4 border rounded-lg hover:bg-gray-50 transition-colors"
              >
                <h3 className="font-semibold text-black mb-2">Newtown Florists</h3>
                <p className="text-sm text-gray-600">Creative, bohemian designs in Sydney's cultural heart</p>
              </Link>
              <Link 
                to="/blog/best-florists-surry-hills-same-day" 
                className="block p-4 border rounded-lg hover:bg-gray-50 transition-colors"
              >
                <h3 className="font-semibold text-black mb-2">Surry Hills Florists</h3>
                <p className="text-sm text-gray-600">Artistic arrangements in Sydney's creative quarter</p>
              </Link>
              <Link 
                to="/blog/best-florists-parramatta-same-day" 
                className="block p-4 border rounded-lg hover:bg-gray-50 transition-colors"
              >
                <h3 className="font-semibold text-black mb-2">Parramatta Florists</h3>
                <p className="text-sm text-gray-600">Professional service in Western Sydney's business hub</p>
              </Link>
            </div>
          </div>

          {/* Testimonial */}
          <div className="bg-neutral-50 rounded-lg p-8 mb-8">
            <div className="text-center">
              <div className="flex justify-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <blockquote className="text-lg italic text-black mb-4">
                "After trying several flower delivery services, I discovered MarketBloom and haven't looked back. The quality difference is remarkable - these Sydney florists are true artists. My weekly office flowers always arrive fresh and beautifully arranged, and the personal service makes all the difference."
              </blockquote>
              <cite className="text-gray-600 font-semibold">
                — James M., Sydney CBD
              </cite>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="bg-white rounded-lg p-8 mb-8 shadow-sm">
            <h2 className="scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight text-black first:mt-0 mb-6">Frequently Asked Questions</h2>
            
            <div className="space-y-4">
              {faqSchema.mainEntity.map((faq, index) => (
                <div key={index} className="border-b border-gray-200 last:border-b-0 pb-4 last:pb-0">
                  <button
                    className="w-full text-left flex justify-between items-center py-2 hover:text-brand-primary transition-colors"
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    aria-expanded={openFaq === index}
                    aria-controls={`faq-answer-${index}`}
                  >
                    <span className="font-semibold text-black pr-4">{faq.name}</span>
                    {openFaq === index ? (
                      <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    )}
                  </button>
                  {openFaq === index && (
                    <div 
                      id={`faq-answer-${index}`}
                      className="mt-2 text-gray-600 leading-relaxed"
                      role="region"
                      aria-labelledby={`faq-question-${index}`}
                    >
                      {faq.acceptedAnswer.text}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Final CTA */}
          <div className="bg-[#967F81] text-white rounded-lg p-6 text-center">
            <h2 className="text-2xl font-bold mb-3">Ready to Experience Sydney's Finest Florists?</h2>
            <p className="text-lg mb-4 opacity-90">Browse our curated network of exceptional Sydney florists</p>
            <Link 
              to={`${sydneyFloristsSearchUrl}&utm_source=seo&utm_medium=landing&utm_campaign=sydney-florists-bottom`}
              className="bg-[#843329] text-white px-8 py-3 rounded-lg font-semibold hover:bg-[#6b2820] transition-colors inline-flex items-center gap-2 min-h-[48px]"
            >
              Shop Sydney Florists
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
} 